import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, Check, Send, Bell, BellOff, Smartphone, Trash2, ShieldAlert, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useAdminRole } from "@/hooks/useAdminRole";
import { toast } from "sonner";

type LogEntry = {
  id: string;
  title: string;
  body: string;
  ts: number;
  state: "foreground" | "background" | "killed" | "sent";
};

type DeliveryType = "alert" | "silent" | "alert_data";

const LOG_KEY = "tecly:push-demo:log";

function detectPlatform(): "ios-web" | "android-web" | "desktop-web" | "ios-native" | "android-native" {
  const cap = (window as any).Capacitor;
  if (cap?.isNativePlatform?.()) {
    return cap.getPlatform?.() === "ios" ? "ios-native" : "android-native";
  }
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios-web";
  if (/Android/i.test(ua)) return "android-web";
  return "desktop-web";
}

export default function PushDemo() {
  const { isSupported, isSubscribed, permission, subscribe, unsubscribe, isLoading } =
    usePushNotifications();

  const [userId, setUserId] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserId(data.user?.id ?? null);
      setAuthChecked(true);
    });
  }, []);
  const { isAdmin, loading: roleLoading } = useAdminRole(userId);

  const [endpoint, setEndpoint] = useState<string>("");
  const [logs, setLogs] = useState<LogEntry[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(LOG_KEY) || "[]");
    } catch {
      return [];
    }
  });
  const [copiedToken, setCopiedToken] = useState(false);

  const [title, setTitle] = useState("Tecly Push Test");
  const [body, setBody] = useState("Funziona anche con l'app chiusa 👋");
  const [delivery, setDelivery] = useState<DeliveryType>("alert");
  const [sending, setSending] = useState(false);

  const platform = useMemo(detectPlatform, []);

  // Get current endpoint as "token"
  useEffect(() => {
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await (reg as any).pushManager.getSubscription();
        if (sub) setEndpoint(sub.endpoint);
      } catch {/* */}
    })();
  }, [isSubscribed]);

  // Listen for SW messages
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handler = (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.type !== "PUSH_RECEIVED") return;
      const entry: LogEntry = {
        id: crypto.randomUUID(),
        title: data.notification?.title || "(no title)",
        body: data.notification?.body || "",
        ts: data.ts || Date.now(),
        state: data.appState,
      };
      setLogs((prev) => {
        const next = [entry, ...prev].slice(0, 50);
        localStorage.setItem(LOG_KEY, JSON.stringify(next));
        return next;
      });
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, []);

  const copyToken = async () => {
    if (!endpoint) return;
    await navigator.clipboard.writeText(endpoint);
    setCopiedToken(true);
    toast.success("Token copiato");
    setTimeout(() => setCopiedToken(false), 1500);
  };

  const clearLog = () => {
    setLogs([]);
    localStorage.removeItem(LOG_KEY);
  };

  // Generated payloads
  const fcmPayload = useMemo(() => {
    const notification = delivery === "silent" ? undefined : { title, body };
    const data =
      delivery === "alert"
        ? undefined
        : { url: "/push-demo", sentAt: new Date().toISOString() };
    return JSON.stringify(
      {
        message: {
          token: "<FCM_DEVICE_TOKEN>",
          ...(notification ? { notification } : {}),
          ...(data ? { data: { ...data, "content-available": "1" } } : { data: { "content-available": "1" } }),
          android: {
            priority: "high", // killed-state: always high
            ...(delivery !== "silent" && {
              notification: { channel_id: "tecly_default", sound: "default" },
            }),
          },
        },
      },
      null,
      2,
    );
  }, [title, body, delivery]);

  const apnsPayload = useMemo(() => {
    const aps: any = { "content-available": 1 }; // killed-state wake
    if (delivery !== "silent") {
      aps.alert = { title, body };
      aps.sound = "default";
      aps.badge = 1;
    }
    const root: any = { aps };
    root.url = "/push-demo";
    root.sentAt = new Date().toISOString();
    return JSON.stringify(
      {
        headers: {
          "apns-priority": "10", // killed-state: always 10
          "apns-push-type": delivery === "silent" ? "background" : "alert",
          "apns-topic": "<your.bundle.id>",
        },
        payload: root,
      },
      null,
      2,
    );
  }, [title, body, delivery]);

  const sendTest = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error("Devi essere loggato per inviare un push di prova");
      return;
    }
    if (!isSubscribed) {
      toast.error("Attiva prima le notifiche");
      return;
    }
    setSending(true);
    try {
      const { error } = await supabase.functions.invoke("send-push-notification", {
        body: {
          type: "user",
          userId: user.id,
          payload: { title, body, url: "/push-demo", tag: "push-demo" },
        },
      });
      if (error) throw error;
      setLogs((prev) => {
        const next = [
          {
            id: crypto.randomUUID(),
            title,
            body,
            ts: Date.now(),
            state: "sent" as const,
          },
          ...prev,
        ].slice(0, 50);
        localStorage.setItem(LOG_KEY, JSON.stringify(next));
        return next;
      });
      toast.success("Inviato! Chiudi l'app per testare lo stato killed.");
    } catch (e: any) {
      toast.error(e.message || "Errore invio");
    } finally {
      setSending(false);
    }
  };

  // Checklist
  const checklist = [
    { label: "Browser supporta Service Worker + Push API", ok: isSupported },
    { label: "Permesso notifiche concesso", ok: permission === "granted" },
    { label: "Sottoscrizione push attiva (token presente)", ok: !!endpoint },
    { label: "Service Worker registrato a /sw.js", ok: "serviceWorker" in navigator },
    {
      label: "HTTPS o localhost (richiesto da Push API)",
      ok: location.protocol === "https:" || location.hostname === "localhost",
    },
    {
      label: "iOS: app installata su Home Screen (PWA)",
      ok: platform === "ios-web" ? (window.matchMedia("(display-mode: standalone)").matches || (navigator as any).standalone === true) : true,
    },
    { label: "VAPID key configurata sul backend", ok: true },
  ];

  const permColor =
    permission === "granted" ? "#22c55e" : permission === "denied" ? "#ef4444" : "#f59e0b";

  // Admin gate
  if (!authChecked || roleLoading) {
    return (
      <div style={{ minHeight: "100dvh", background: "#0b1226", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, opacity: .7 }}>
        Verifica permessi…
      </div>
    );
  }
  if (!userId || !isAdmin) {
    return (
      <div style={{ minHeight: "100dvh", background: "linear-gradient(180deg, #0b1226 0%, #0f172a 100%)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ maxWidth: 380, textAlign: "center", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 20, padding: 28, backdropFilter: "blur(20px)" }}>
          <div style={{ display: "inline-flex", padding: 14, borderRadius: 999, background: "rgba(239,68,68,0.15)", color: "#ef4444", marginBottom: 14 }}>
            <ShieldAlert size={28} />
          </div>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 8px" }}>Accesso negato</h1>
          <p style={{ fontSize: 13, opacity: .7, margin: "0 0 18px" }}>
            Questa sezione è riservata agli amministratori Tecly. {userId ? "Il tuo account non ha i permessi necessari." : "Effettua l'accesso con un account admin."}
          </p>
          <Link to="/" style={{ display: "inline-block", padding: "10px 18px", background: "linear-gradient(135deg,#3b82f6,#38bdf8)", color: "white", borderRadius: 14, fontWeight: 600, fontSize: 14, textDecoration: "none" }}>
            Torna alla home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100dvh", background: "linear-gradient(180deg, #0b1226 0%, #0f172a 100%)", color: "white", paddingBottom: "120px" }}>
      <style>{`
        .pd-card { background: rgba(255,255,255,0.06); backdrop-filter: blur(20px) saturate(160%); border: 1px solid rgba(255,255,255,0.1); border-radius: 20px; padding: 18px; }
        .pd-input { width:100%; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12); color: white; border-radius: 12px; padding: 10px 14px; font-size: 14px; outline: none; }
        .pd-input:focus { border-color: rgba(56,189,248,0.6); }
        .pd-btn { display:inline-flex; align-items:center; gap:8px; background: linear-gradient(135deg,#3b82f6,#38bdf8); color:white; border:none; padding: 10px 16px; border-radius: 14px; font-weight: 600; font-size: 14px; cursor:pointer; transition: transform .15s; }
        .pd-btn:active { transform: scale(0.96); }
        .pd-btn[disabled] { opacity:.5; cursor: not-allowed; }
        .pd-btn-ghost { background: rgba(255,255,255,0.08); }
        .pd-pill { display:inline-flex; align-items:center; gap:6px; padding:4px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; }
        .pd-seg { display:flex; background: rgba(255,255,255,0.06); border-radius:12px; padding: 4px; gap: 4px; }
        .pd-seg button { flex:1; padding:8px 10px; border:none; background:transparent; color: rgba(255,255,255,0.7); border-radius: 9px; font-size: 12px; font-weight: 600; cursor:pointer; }
        .pd-seg button.active { background: rgba(59,130,246,0.7); color: white; }
        .pd-code { background: #020617; border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 12px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11.5px; line-height: 1.55; color: #cbd5e1; overflow-x: auto; white-space: pre; }
        .pd-grid { display: grid; grid-template-columns: 1fr; gap: 14px; }
        @media (min-width: 720px) { .pd-grid-2 { grid-template-columns: 1fr 1fr; } }
        .pd-row { display:flex; align-items:center; justify-content:space-between; gap: 12px; padding: 10px 0; border-bottom: 1px solid rgba(255,255,255,0.06); }
        .pd-row:last-child { border-bottom: none; }
      `}</style>

      <header style={{ position: "sticky", top: 0, zIndex: 10, padding: "calc(env(safe-area-inset-top,0px) + 12px) 16px 12px", background: "rgba(11,18,38,0.7)", backdropFilter: "blur(20px)", display: "flex", alignItems: "center", gap: 12, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <Link to="/" aria-label="Indietro" className="lg-icon-btn" style={{ width: 38, height: 38, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 12, color: "white" }}>
          <ArrowLeft size={20} />
        </Link>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Push Notifications Demo</div>
          <div style={{ fontSize: 12, opacity: 0.6 }}>Foreground · Background · Killed</div>
        </div>
      </header>

      <main style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14, maxWidth: 880, margin: "0 auto" }}>
        {/* Dashboard */}
        <section className="pd-card">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <Bell size={18} />
            <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Stato dispositivo</h2>
          </div>
          <div className="pd-row">
            <span style={{ fontSize: 13, opacity: .8 }}>Piattaforma rilevata</span>
            <span className="pd-pill" style={{ background: "rgba(59,130,246,0.2)" }}>
              <Smartphone size={12} /> {platform}
            </span>
          </div>
          <div className="pd-row">
            <span style={{ fontSize: 13, opacity: .8 }}>Permesso notifiche</span>
            <span className="pd-pill" style={{ background: `${permColor}33`, color: permColor }}>
              {permission}
            </span>
          </div>
          <div className="pd-row">
            <span style={{ fontSize: 13, opacity: .8 }}>Sottoscrizione</span>
            {isSubscribed ? (
              <button className="pd-btn pd-btn-ghost" onClick={unsubscribe} disabled={isLoading}>
                <BellOff size={14} /> Disattiva
              </button>
            ) : (
              <button className="pd-btn" onClick={subscribe} disabled={isLoading || !isSupported}>
                <Bell size={14} /> Attiva
              </button>
            )}
          </div>
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, opacity: .7, marginBottom: 6 }}>
              Push token / endpoint {platform.endsWith("native") ? "(FCM/APNs)" : "(Web Push)"}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <code style={{ flex: 1, background: "#020617", borderRadius: 10, padding: "10px 12px", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", border: "1px solid rgba(255,255,255,0.08)" }}>
                {endpoint || "— nessun token, attiva le notifiche —"}
              </code>
              <button className="pd-btn pd-btn-ghost" onClick={copyToken} disabled={!endpoint}>
                {copiedToken ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          </div>
        </section>

        {/* Tester */}
        <section className="pd-card">
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 10px" }}>Tester notifiche (admin)</h2>
          <div style={{ display: "flex", gap: 10, padding: 10, background: "rgba(56,189,248,0.08)", border: "1px solid rgba(56,189,248,0.25)", borderRadius: 12, marginBottom: 12 }}>
            <Zap size={16} style={{ color: "#38bdf8", flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 11.5, lineHeight: 1.5, opacity: .85 }}>
              <strong>Consegna in stato killed:</strong> il backend invia con header <code>Urgency: high</code> (Web Push), <code>priority: high</code> (FCM) e <code>apns-priority: 10</code> + <code>content-available: 1</code> (APNs). Questo forza iOS/Android a risvegliare il sistema e mostrare la notifica anche con l'app completamente chiusa.
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <input className="pd-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titolo" />
            <textarea className="pd-input" rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Messaggio" />
            <div className="pd-seg">
              {(["alert", "silent", "alert_data"] as DeliveryType[]).map((d) => (
                <button key={d} className={delivery === d ? "active" : ""} onClick={() => setDelivery(d)}>
                  {d === "alert" ? "Alert" : d === "silent" ? "Silent (data)" : "Alert + Data"}
                </button>
              ))}
            </div>
            <button className="pd-btn" onClick={sendTest} disabled={sending}>
              <Send size={14} /> {sending ? "Invio…" : "Invia push di prova"}
            </button>
            <p style={{ fontSize: 11.5, opacity: .6, margin: "4px 2px 0" }}>
              Per testare lo stato <strong>killed</strong>: invia, poi chiudi completamente l'app entro pochi secondi. La notifica arriverà dal sistema operativo anche con il processo terminato.
            </p>
          </div>
        </section>

        {/* Payloads */}
        <section className="pd-grid pd-grid-2">
          <div className="pd-card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>FCM (Android)</h3>
              <span className="pd-pill" style={{ background: "rgba(34,197,94,0.2)", color: "#22c55e" }}>HTTP v1</span>
            </div>
            <pre className="pd-code">{fcmPayload}</pre>
          </div>
          <div className="pd-card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>APNs (iOS)</h3>
              <span className="pd-pill" style={{ background: "rgba(56,189,248,0.2)", color: "#38bdf8" }}>HTTP/2</span>
            </div>
            <pre className="pd-code">{apnsPayload}</pre>
          </div>
        </section>

        {/* Log */}
        <section className="pd-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Log notifiche ricevute</h2>
            <button className="pd-btn pd-btn-ghost" onClick={clearLog} disabled={!logs.length}>
              <Trash2 size={14} /> Pulisci
            </button>
          </div>
          {logs.length === 0 ? (
            <p style={{ opacity: .5, fontSize: 13, margin: "8px 0" }}>Nessuna notifica ancora registrata.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {logs.map((l) => {
                const color =
                  l.state === "foreground" ? "#22c55e" :
                  l.state === "background" ? "#f59e0b" :
                  l.state === "killed" ? "#ef4444" : "#3b82f6";
                return (
                  <div key={l.id} style={{ display: "flex", gap: 10, padding: 10, background: "rgba(255,255,255,0.04)", borderRadius: 12, border: "1px solid rgba(255,255,255,0.06)" }}>
                    <div style={{ width: 8, borderRadius: 4, background: color, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                        <strong style={{ fontSize: 13 }}>{l.title}</strong>
                        <span className="pd-pill" style={{ background: `${color}26`, color }}>{l.state}</span>
                      </div>
                      <div style={{ fontSize: 12, opacity: .75, marginTop: 2 }}>{l.body}</div>
                      <div style={{ fontSize: 10.5, opacity: .5, marginTop: 4 }}>{new Date(l.ts).toLocaleString()}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Checklist */}
        <section className="pd-card">
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 10px" }}>Checklist configurazione</h2>
          <div>
            {checklist.map((c, i) => (
              <div key={i} className="pd-row">
                <span style={{ fontSize: 13 }}>{c.label}</span>
                <span className="pd-pill" style={{ background: c.ok ? "rgba(34,197,94,0.18)" : "rgba(239,68,68,0.18)", color: c.ok ? "#22c55e" : "#ef4444" }}>
                  {c.ok ? "OK" : "Mancante"}
                </span>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 11.5, opacity: .6, marginTop: 10 }}>
            Per FCM/APNs nativi reali serve build Capacitor con plugin <code>@capacitor/push-notifications</code>, Firebase project (google-services.json / GoogleService-Info.plist) e Apple Push key (.p8) caricata su Firebase.
          </p>
        </section>
      </main>
    </div>
  );
}
